import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { backends } from '@/api/backends';
import { request } from '@/api/client';
import type { MercedesServiceStatus, Vehicle, VehicleDetail, VehicleInput } from './types';

export const VEHICLES_KEY = ['vehicles'] as const;
const BASE = '/dispo/vehicles';

// numeric: natural number order („Sprinter 2" before „Sprinter 10"), PO 2026-10-12.
const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

/**
 * The one list order: displayName, German collation, blind to case and accents („Ärger" next to
 * „arger"), id for equal names. Applied to every list the cache holds, so neither the BE's
 * collation and tie order nor a seeded insert can move a row.
 */
function byDisplayName(a: Vehicle, b: Vehicle): number {
    return collator.compare(a.displayName, b.displayName) || a.id.localeCompare(b.id);
}

const sortByDisplayName = (rows: Vehicle[]) => [...rows].sort(byDisplayName);

export function useVehicles() {
    return useQuery({
        queryKey: VEHICLES_KEY,
        queryFn: () => request<Vehicle[]>(backends.core, BASE),
        select: sortByDisplayName,
    });
}

export function useVehicle(id: string | null) {
    return useQuery({
        queryKey: [...VEHICLES_KEY, id],
        queryFn: () => request<VehicleDetail>(backends.core, `${BASE}/${id}`),
        enabled: id !== null,
    });
}

export function useMercedesStatus(id: string, enabled: boolean) {
    return useQuery({
        queryKey: [...VEHICLES_KEY, id, 'mercedesStatus'],
        queryFn: () => request<MercedesServiceStatus[]>(backends.core, `${BASE}/${id}/mercedes/status`),
        enabled,
        // A 502/503 must show the degraded state at once, not after retries.
        retry: false,
    });
}

/**
 * Every mutation refetches the list, the detail and the status (prefix key), also after a failure.
 * Each mutation first writes what the BE returned into the caches it can fill (`seed`), so the UI
 * never shows the pre-mutation record while that refetch is still running. The invalidation is
 * fire-and-forget: a returned promise would be awaited by TanStack and the success callbacks
 * (dialog close, snackbar) would wait for every refetch to finish.
 */
function useInvalidatingMutation<TData, TVars>(
    mutationFn: (vars: TVars) => Promise<TData>,
    seed?: (queryClient: QueryClient, data: TData, vars: TVars) => void,
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: (data, vars) => seed?.(queryClient, data, vars),
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: VEHICLES_KEY });
        },
    });
}

/**
 * Upsert the vehicle's row in the cached list (exact key, so the detail/status queries are untouched).
 * Only when a list is cached — a single row must never pose as the whole fleet before the first load.
 * Inserts append; `useVehicles` orders every list with `byDisplayName`.
 */
function seedListRow(queryClient: QueryClient, vehicle: Vehicle) {
    // A list in error state stays in error (the page shows the alert, not stale rows) until a
    // full refetch succeeds; setQueryData would flip it back to success with one fresh row.
    if (queryClient.getQueryState(VEHICLES_KEY)?.status === 'error') {
        return;
    }
    queryClient.setQueryData<Vehicle[]>(VEHICLES_KEY, (rows) => {
        if (!rows) {
            return rows;
        }
        return rows.some((row) => row.id === vehicle.id)
            ? rows.map((row) => (row.id === vehicle.id ? { ...row, ...vehicle } : row))
            : [...rows, vehicle];
    });
}

function evictListRow(queryClient: QueryClient, id: string) {
    queryClient.removeQueries({ queryKey: [...VEHICLES_KEY, id] });
    if (queryClient.getQueryState(VEHICLES_KEY)?.status === 'error') {
        return; // same rule as seedListRow: an errored list waits for a full refetch
    }
    queryClient.setQueryData<Vehicle[]>(VEHICLES_KEY, (rows) => rows?.filter((row) => row.id !== id));
}

/** The list cache models the VIN-free list response — never let a detail's VIN leak into it. */
const toRow = ({ id, displayName, licensePlate, notes, available, mercedesStatus }: VehicleDetail): Vehicle => ({
    id,
    displayName,
    licensePlate,
    notes,
    available,
    mercedesStatus,
});

function seedDetail(queryClient: QueryClient, detail: VehicleDetail) {
    queryClient.setQueryData([...VEHICLES_KEY, detail.id], detail);
    seedListRow(queryClient, toRow(detail));
}

export function useSetAvailability() {
    return useInvalidatingMutation(
        ({ id, available }: { id: string; available: boolean }) =>
            request<Vehicle>(backends.core, `${BASE}/${id}/availability`, { method: 'PUT', body: { available } }),
        seedListRow,
    );
}

export function useCreateVehicle() {
    return useInvalidatingMutation(
        (input: VehicleInput) => request<VehicleDetail>(backends.core, BASE, { method: 'POST', body: input }),
        seedDetail,
    );
}

export function useUpdateVehicle() {
    return useInvalidatingMutation(
        ({ id, ...input }: { id: string } & Partial<VehicleInput>) =>
            request<VehicleDetail>(backends.core, `${BASE}/${id}`, { method: 'PUT', body: input }),
        seedDetail,
    );
}

export function useDeleteVehicle() {
    return useInvalidatingMutation(
        (id: string) => request<VehicleDetail>(backends.core, `${BASE}/${id}`, { method: 'DELETE' }),
        (queryClient, _archived, id) => evictListRow(queryClient, id),
    );
}

export function useActivateMercedes() {
    return useInvalidatingMutation(
        (id: string) =>
            request<MercedesServiceStatus[]>(backends.core, `${BASE}/${id}/mercedes/activate`, { method: 'POST' }),
        // A successful activate means the vehicle is CONNECTED (BE flow step 5); seed that state so
        // delete and VIN edit lock at once, and the live service status the BE answered with.
        (queryClient, status, id) => {
            queryClient.setQueryData([...VEHICLES_KEY, id, 'mercedesStatus'], status);
            const detail = queryClient.getQueryData<VehicleDetail>([...VEHICLES_KEY, id]);
            if (detail) {
                seedDetail(queryClient, { ...detail, mercedesStatus: 'CONNECTED' });
            }
        },
    );
}

export function useDeactivateMercedes() {
    return useInvalidatingMutation(
        (id: string) => request<VehicleDetail>(backends.core, `${BASE}/${id}/mercedes/deactivate`, { method: 'POST' }),
        seedDetail,
    );
}
